var Component       = require("enyo/Component"),
    PerformanceMode = require("enyo-perf/PerformanceMode"),
    kind            = require("enyo/kind"),
    utils           = require("enyo/utils");

require("enyo-ui");

/**
 * [DrawLoop]{@link enyo-ui.DrawLoop} allows registering multiple callbacks to a
 * queue, executing one or more of them per animationFrame. It uses
 * `enyo-anim.AnimationLoop` to request animationFrames.
 *
 * When PerformanceMode is enabled, only one task is allowed to execute per
 * frame in order to try and minimize performance impact. When PerformanceMode
 * is disabled, tasks are executed one at a time until a total execution time of
 * 16ms has been exceeded. Once that happens, the rest of the tasks will be
 * deferred to future animationFrames.
 *
 * @class   enyo-ui.DrawLoop
 * @extends enyo.Component
 * @public
 */
module.exports = kind.singleton(/** @lends enyo-ui.DrawLoop.prototype */ {

    /**
     * @private
     */
    name: "enyo-ui.DrawLoop",

    /**
     * @private
     */
    kind: Component,

    /**
     * A hash of named callbacks.
     *
     * @type {Object}
     * @private
     */
    callbacks: null,

    /**
     * The amount of registered callbacks.
     *
     * @type {number}
     * @private
     */
    length: 0,

    /**
     * Whether to allow drawing in performanceMode.
     *
     * @type {boolean}
     * @public
     */
    drawInPerformanceMode: false,

    /**
     * The timeout that will trigger the executing of the next callback in the
     * queue.
     *
     * @type {timeoutID}
     * @private
     */
    timeoutID: null,

    /**
     * Sets up a hash to store callbacks.
     *
     * @memberof enyo-ui.DrawLoop
     * @method
     * @private
     */
    importProps: kind.inherit(function(sup) {
        return function() {
            this.euid      = this.euid || utils.uid();
            this.callbacks = {};
            sup.apply(this, arguments);
        };
    }),

    /**
     * @memberof enyo-ui.DrawLoop
     * @method
     * @private
     */
    constructed: kind.inherit(function(sup) {
        return function() {
            this.tick = this.bindSafely(this.tick);
            sup.apply(this, arguments);
        };
    }),

    /**
     * Request a callback to be executed on the next animationFrame.
     * Automatically schedules an animationFrame if none is scheduled yet.
     *
     * @param {string}    name      The name
     * @param {Function}  callback  The callback
     * @public
     */
    request: function(name, callback) {
        if (!this.isScheduled(name)) {
            this.set("length", this.length + 1);
        }

        this.callbacks[name] = callback;

        if (this.timeoutID === null) {
            this.timeoutID = setTimeout(this.tick, 0);
        }
    },

    /**
     * Determines if the named callback is scheduled to be executed on the next
     * animationFrame.
     *
     * @param  {string}   name  The name
     * @return {boolean}  `true` if scheduled, `false` otherwise.
     * @public
     */
    isScheduled: function(name) {
        return Boolean(this.callbacks[name]);
    },

    /**
     * Removes the callback.
     *
     * @param {string}  name  The name
     * @public
     */
    cancel: function(name) {
        if (this.isScheduled(name)) {
            this.set("length", this.length - 1);
        }

        delete this.callbacks[name];

        if (this.length === 0 && this.timeoutID !== null) {
            clearTimeout(this.timeoutID);
            this.timeoutID = null;
        }
    },

    /**
     * Executes one callback on every animationFrame tick, schedules next
     * animationFrame as needed.
     *
     * @private
     */
    tick: function() {
        var callbacks             = this.callbacks,
            performanceMode       = PerformanceMode.on,
            drawInPerformanceMode = this.get("drawInPerformanceMode"),
            name,
            callback;

        this.timeoutID = null;

        /* eslint-disable guard-for-in */
        for (name in callbacks) {
            // Don't draw during performance mode
            if (!performanceMode || drawInPerformanceMode) {
                callback = callbacks[name];
                this.set("length", this.length - 1);
                delete callbacks[name];
                callback();
            }
            break;
        }
        /* eslint-enable guard-for-in */

        if (this.length && this.timeoutID === null) {
            this.timeoutID = setTimeout(this.tick, 0);
        }
    },

    /**
     * @private
     */
    lengthChanged: function() {
        var isDrawing = this.length > 0;

        if (this.drawing !== isDrawing) {
            this.set("drawing", isDrawing);
        }
    }

});
